export default class Statistics {
    constructor(Rest) {
        this.rest = Rest.rest;
    }

    /**
     * General statistics for an account displayed on the feed.
     *
     * @returns Object with dispatchers and total counts
     * @param interval start- and endDate for the statistic-span.
     */
    dashboardStatistics(interval) {
        var dates = { startDate: null, endDate: null };

        if (interval) {
            dates = this.formatUTCDates(interval.startDate, interval.endDate);
        }

        return this.rest.one('statistics').one('dashboard').get({ startDate: dates.startDate, endDate: dates.endDate });
    };

    getAbTestStatistics(abTestId, settings, type) {
        var dates = { startDate: null, endDate: null };

        if (settings.startDate || settings.endDate) {
            dates = this.formatUTCDates(settings.startDate, settings.endDate);
        }

        return this.rest.one('statistics', abTestId).one('abTest').get({
            type: type,
            startDate: dates.startDate,
            endDate: dates.endDate,
            selection: settings.selection
        });
    };

    getAbStatusCounts(abTestId, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', abTestId).one('abTestCounts').get({
            startDate: dates.startDate,
            endDate: dates.endDate,
            currentLinkId: settings.currentLinkId,
            type: settings.type,
            "disabledDispatchers[]": settings.disabledDispatchers
        });
    };

    getStatistics(dispatcherId, type, statisticType, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        settings.startDate = dates.startDate;
        settings.endDate = dates.endDate;

        return this.rest.all('statistics/statistics').post({
            dispatcherId: dispatcherId,
            settings: settings,
            statistic_type: statisticType,
            type: type,
            selection: settings.selection
        });
    };

    /**
     * @param dispatcherId
     * @returns campaign UTM
     */
    getCampaignUtm(dispatcherId) {
        return this.rest.one('statistics/campaign', dispatcherId).one('utm').get();
    };

    /**
     *
     * @param emailId
     * @returns email UTM
     */
    getEmailUtm(emailId) {
        return this.rest.one('statistics/email', emailId).one('utm').get();
    };

    /**
     * Store Google Analytics goals for Campaign
     */
    storeGoals(goals, utm, dates, profileId, campaign) {
        return this.rest.all('statistics/google/storeGoals').post({
            goals : goals,
            utm : utm,
            dates: dates,
            profileId : profileId,
            campaign: campaign
        });
    };

    /**
     * Get cached Google Analytics goals for campaign from Cache
     */
    getCachedGoal(utm, dates, profileId, campaign) {
        return this.rest.one('statistics').one('google').one('getGoals').one('utm', utm).one('dates', dates).one('profileId', profileId).one('campaign', campaign).get();
    };

    /**
     *
     * Store Google Analytics goals for ABTest
     *
     * @param goals
     * @param campaigns
     * @param dates
     * @param profileId
     * @returns {*}
     */
    storeAbGoals(goals, utms, dates, profileId, ab) {
        return this.rest.all('statistics/google/storeGoals/abtest').post({
            goals : goals,
            utms : utms,
            profileId : profileId,
            dates : dates,
            ab: ab
        });
    };

    getCachedAbGoal(utms, dates, profileId, ab) {
        return this.rest.one('statistics')
            .one('google')
            .one('getAbGoals')
            .one('utms', utms)
            .one('dates', dates)
            .one('profileId', profileId)
            .one('ab', ab).get();
    };

    getStatisticsSubscribers(dispatcherId, type, settings, statisticType, page, search) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);
        search = search || null;

        settings.startDate = dates.startDate;
        settings.endDate = dates.endDate;

        return this.rest.all('statistics/subscribers').post({
            dispatcherId: dispatcherId,
            type: type,
            settings: settings,
            page: page || 1,
            statistic_type: statisticType,
            search: search
        });
    };

    getAbTestSubscribers(abTestId, settings, status, page, search) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', abTestId).one('abTestSubscribers').get({
            startDate: dates.startDate,
            endDate: dates.endDate,
            statistic_type: status,
            page: page || 1,
            search: search || null,
            "disabledDispatchers[]": settings.disabledDispatchers
        });
    };

    getStatusCounts(id, dispatcherType, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        settings.startDate = dates.startDate;
        settings.endDate = dates.endDate;

        return this.rest.all('statistics/getCounts').post({
            dispatcherId: id,
            type: dispatcherType,
            settings: settings
        });
    };

    subscriberFlow(settings) {
        var dates;

        if (settings.dates) {
            dates = this.formatUTCDates(settings.dates.startDate, settings.dates.endDate);
        } else {
            dates = { startDate: null, endDate: null };
        }

        settings.startDate = dates.startDate;
        settings.endDate = dates.endDate;

        return this.rest.all('statistics/subscriberFlow').post({ settings: settings });
    };

    getLinks(dispatcherId, type, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', dispatcherId).one('links').get({
            type: type,
            startDate: dates.startDate,
            endDate: dates.endDate,
            messageType: settings.messageType
        });
    };

    abTestSubscribersByLink(abTestId, link, linkId, settings, page) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', abTestId).one('abTestSubscribersByLink').get({
            linkId: linkId,
            startDate: dates.startDate,
            endDate: dates.endDate,
            linkName: link,
            page: page || 1,
            "disabledDispatchers[]": settings.disabledDispatchers
        });
    };

    getLinksByAbTest(abTestId, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', abTestId).one('abTestLinks').get({
            startDate: dates.startDate,
            endDate: dates.endDate,
            "disabledDispatchers[]": settings.disabledDispatchers
        });
    };

    getSubscribersByLink(dispatcherId, type, linkName, linkId, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', dispatcherId).one('linkSubscribers').get({
            type: type,
            linkName: linkName,
            linkId: linkId,
            page: settings.currentPage,
            startDate: dates.startDate,
            endDate: dates.endDate,
            messageType: settings.messageType
        });
    };

    widgetStatistics(dispatcherId, type) {
        if (type === 'abTest') {
            return this.rest.one('statistics', dispatcherId).one('abTestWidgetStats').get();
        } else {
            return this.rest.one('statistics', dispatcherId).one('widgetStats').get({ type: type });
        }
    };

    abTestDeviceStats(abTestId, status, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', abTestId).one('abTestDevices').get({
            startDate: dates.startDate,
            endDate: dates.endDate,
            statistic_type: status,
            selection: settings.selection
        });
    };

    getDeviceStats(dispatcherId, dispatcherType, statisticType, settings) {
        var dates = this.formatUTCDates(settings.startDate, settings.endDate);

        return this.rest.one('statistics', dispatcherId).one('devices').get({
            type: dispatcherType,
            statistic_type: statisticType,
            startDate: dates.startDate,
            endDate: dates.endDate,
            selection: settings.selection
        });
    };

    getCollectionUsageByCampaign(campaignId) {
        return this.rest.one('statistics', campaignId).one('collectionsByCampaign').get();
    };

    exportSubscribers(exportSettings) {
        return this.rest.all('statistics/export').post({
            toTag: exportSettings.toTag,
            tagIds: exportSettings.tagIds,
            statisticType: exportSettings.statisticType,
            campaignId: exportSettings.campaignId,
            hasStatistic: exportSettings.hasStatistic,
            total: exportSettings.total,
            link: exportSettings.link,
            isTransactional: false,
            isAbTest: exportSettings.abTest,
            "disabledDispatchers[]": exportSettings.disabledDispatchers,
            startDate: exportSettings.startDate,
            endDate: exportSettings.endDate,
        });
    };

    exportTransactionalSubscribers(exportSettings) {
        return this.rest.all('statistics/export').post({
            toTag: exportSettings.toTag,
            tagIds: exportSettings.tagIds,
            statisticType: exportSettings.statisticType,
            messageId: exportSettings.messageId,
            messageType: exportSettings.messageType,
            automationId: exportSettings.automationId,
            hasStatistic: exportSettings.hasStatistic,
            total: exportSettings.total,
            link: exportSettings.link,
            isAutomation: exportSettings.isAutomation,
            isTransactional: exportSettings.isTransactional,
            isAbTest: exportSettings.abTest,
            "disabledDispatchers[]": exportSettings.disabledDispatchers,
            startDate: exportSettings.startDate,
            endDate: exportSettings.endDate,
        });
    };

    findExportFile(dispatcherId) {
        return this.rest.one('statistics', dispatcherId).one('findExportFile').get();
    };

    exportProgress(dispatcherId, section = 'campaign') {
        return this.rest.one('statistics', dispatcherId).one(section).one('exportProgress').get();
    };

    /**
     * Format dates to UTC to always get the right time-span for statistics.
     *
     * @param startDate
     * @param endDate
     * @returns {{startDate: null|Date, endDate: null|Date}}
     */
    formatUTCDates(startDate, endDate) {
        if (startDate) {
            startDate = new Date(startDate);
            startDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
        }
        if (endDate) {
            endDate = new Date(endDate);
            endDate = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0));
        }
        return { startDate: startDate, endDate: endDate };
    };
};